// ── 출퇴근 시간관리 모달 ──────────────────────────────────────────────────────
import React from 'react';
import Modal from '../../../components/Modal';
import { WEEKDAY_ORDER, WEEKDAY_LABELS, normalizePolicyMinutesInput } from './utils';
import { formatTimeTyping } from '../../../utils/timeInput';
import {
  btn, primaryBtn, inputStyle, fieldStyle, labelStyle,
  timeSettingWrapStyle, timePolicyCardStyle, timePolicyGridStyle,
  timeSettingCardStyle, timeSettingHeaderStyle, timeWeekTableStyle,
  timeWeekRowStyle, timeDayCellStyle, timeWorkdayToggleStyle,
  timeWeekInputStyle, timeRangeMarkStyle, annualLeavePreviewStyle,
} from './styles';

function getWorkLocationLabels(items = []) {
  const labels = new Set();
  (items || []).forEach((item) => {
    if (item?.is_active === false) return;
    const label = String(item?.label || item?.alias || '').trim();
    if (label) labels.add(label);
  });
  return [...labels].sort((a, b) => a.localeCompare(b, 'ko'));
}

/**
 * 출퇴근 시간관리 모달 컴포넌트
 * Props:
 *   open                — 모달 오픈 여부
 *   loading             — 데이터 로딩 중
 *   saving              — 저장 중
 *   draftRows           — 템플릿 드래프트 행 배열
 *   policy              — { late_grace_minutes, early_leave_grace_minutes }
 *   templateWeeklyPreview — 주간 소정근로시간 미리보기 배열
 *   onClose             — 닫기 콜백
 *   onSave              — 저장 콜백
 *   onAddRow            — 행 추가 콜백
 *   onPatchRow(rowKey, field, value) — 행 필드 수정 콜백
 *   onPatchRule(rowKey, dayKey, field, value) — 요일 규칙 수정 콜백
 *   onRemoveRow(rowKey) — 행 삭제 콜백
 *   onPolicyChange(updates) — 정책 필드 변경 콜백
 */
export function TimeSettingsModal({
  open, loading, saving, draftRows, policy, templateWeeklyPreview,
  onClose, onSave, onAddRow, onPatchRow, onPatchRule, onRemoveRow, onPolicyChange,
  workLocationOptions = [],
}) {
  const workLocationLabels = getWorkLocationLabels(workLocationOptions);

  return (
    <Modal
      open={open}
      icon="⏰"
      title="출퇴근 시간관리"
      onClose={() => { if (saving) return; onClose(); }}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={btn} onClick={onClose} disabled={saving}>닫기</button>
          <button style={primaryBtn} onClick={onSave} disabled={saving || loading}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      )}
      width="min(960px, 96vw)"
    >
      {loading ? (
        <div style={{ color: 'var(--text-muted)' }}>불러오는 중...</div>
      ) : (
        <div style={timeSettingWrapStyle}>
          {/* 지각/조퇴 기준 정책 */}
          <div style={timePolicyCardStyle}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>지각/조퇴 기준</div>
            <div style={timePolicyGridStyle}>
              <label style={fieldStyle}>
                <span style={labelStyle}>지각 기준(분)</span>
                <input
                  style={inputStyle} type="number" min="0" max="180"
                  value={policy.late_grace_minutes}
                  onChange={(e) => onPolicyChange({ late_grace_minutes: normalizePolicyMinutesInput(e.target.value) })}
                />
              </label>
              <label style={fieldStyle}>
                <span style={labelStyle}>조퇴 기준(분)</span>
                <input
                  style={inputStyle} type="number" min="0" max="180"
                  value={policy.early_leave_grace_minutes}
                  onChange={(e) => onPolicyChange({ early_leave_grace_minutes: normalizePolicyMinutesInput(e.target.value) })}
                />
              </label>
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>기준 이하는 지각/조퇴로 집계하지 않습니다.</div>
          </div>

          {/* 근무 유형 템플릿 목록 */}
          {draftRows.map((row) => (
            <div key={row._rowKey} style={timeSettingCardStyle}>
              <div style={timeSettingHeaderStyle}>
                <input
                  style={{ ...inputStyle, maxWidth: 280 }}
                  value={row.name}
                  onChange={(e) => onPatchRow(row._rowKey, 'name', e.target.value)}
                  placeholder="근무유형명"
                />
                <button style={btn} onClick={() => onRemoveRow(row._rowKey)}>삭제</button>
              </div>

              {/* 계약상 1일 소정근로시간 — 요일별 시간이 불균등할 때만 입력(미입력=자동) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '2px 0 8px' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>1일 소정근로시간</span>
                <input
                  style={{ ...inputStyle, width: 90, textAlign: 'right' }}
                  type="text"
                  inputMode="decimal"
                  value={row.prescribed_daily_hours ?? ''}
                  placeholder="자동"
                  onChange={(e) => onPatchRow(row._rowKey, 'prescribed_daily_hours', e.target.value.replace(/[^0-9.]/g, ''))}
                />
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  시간 · 비워두면 주 소정근로시간 ÷ 근무일수로 자동 계산. 근로계약상 1일 근로시간이 요일별 배치와
                  다를 때만 입력합니다(통상시급·통상일급·주휴수당 기준).
                </span>
              </div>

              {/* 요일별 근무 규칙 */}
              <div style={timeWeekTableStyle}>
                {WEEKDAY_ORDER.map((dayKey) => {
                  const dayRule = row.weekly_rules?.[dayKey] || {
                    is_workday: false,
                    start_time: '',
                    end_time: '',
                    break_minutes: null,
                    work_location: null,
                  };
                  const currentWorkLocation = String(dayRule.work_location || '').trim();
                  const currentMissing = currentWorkLocation && !workLocationLabels.includes(currentWorkLocation);
                  return (
                    <div key={`${row._rowKey}-${dayKey}`} style={timeWeekRowStyle}>
                      {/* 요일 레이블 */}
                      <div style={timeDayCellStyle}>{WEEKDAY_LABELS[dayKey]}</div>
                      {/* 근무 여부 체크박스 */}
                      <label style={timeWorkdayToggleStyle}>
                        <input
                          type="checkbox"
                          checked={Boolean(dayRule.is_workday)}
                          onChange={(e) => onPatchRule(row._rowKey, dayKey, 'is_workday', e.target.checked)}
                        />
                        <span>근무</span>
                      </label>
                      {/* 출근 시간 */}
                      <select
                        style={timeWeekInputStyle}
                        value={currentWorkLocation}
                        disabled={!dayRule.is_workday}
                        onChange={(e) => onPatchRule(row._rowKey, dayKey, 'work_location', e.target.value)}
                      >
                        <option value="">근무지 미지정</option>
                        {currentMissing && (
                          <option value={currentWorkLocation}>{`현재값: ${currentWorkLocation} (IP 별칭에 없음)`}</option>
                        )}
                        {workLocationLabels.map((label) => (
                          <option key={label} value={label}>{label}</option>
                        ))}
                      </select>
                      <input
                        style={timeWeekInputStyle} type="text" inputMode="numeric"
                        maxLength={5} placeholder="09:30" value={dayRule.start_time}
                        disabled={!dayRule.is_workday}
                        onChange={(e) => onPatchRule(row._rowKey, dayKey, 'start_time', formatTimeTyping(e.target.value))}
                      />
                      <span style={timeRangeMarkStyle}>~</span>
                      {/* 퇴근 시간 */}
                      <input
                        style={timeWeekInputStyle} type="text" inputMode="numeric"
                        maxLength={5} placeholder="18:00" value={dayRule.end_time}
                        disabled={!dayRule.is_workday}
                        onChange={(e) => onPatchRule(row._rowKey, dayKey, 'end_time', formatTimeTyping(e.target.value))}
                      />
                      {/* 소정 휴게시간 — 빈값=미설정(연차 계산 시 근로기준법 자동 적용) */}
                      <span style={{ color: 'var(--text-muted)', fontSize: 11, whiteSpace: 'nowrap' }}>휴게</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
                        <input
                          style={{ ...timeWeekInputStyle, textAlign: 'right', MozAppearance: 'textfield', WebkitAppearance: 'none' }}
                          type="text" inputMode="numeric" maxLength={3} placeholder="자동"
                          value={dayRule.break_minutes === null || dayRule.break_minutes === undefined ? '' : dayRule.break_minutes}
                          disabled={!dayRule.is_workday}
                          onChange={(e) => onPatchRule(row._rowKey, dayKey, 'break_minutes', e.target.value.replace(/[^0-9]/g, ''))}
                        />
                        <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>분</span>
                      </div>
                      {/* 반차 기준시각 — 빈값=자동계산(시작·종료 중간값), 입력 시 반차(전)/(후) 분기점 */}
                      <span style={{ color: 'var(--text-muted)', fontSize: 11, whiteSpace: 'nowrap' }}>반차기준</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                        <input
                          style={timeWeekInputStyle} type="text" inputMode="numeric"
                          maxLength={5} placeholder="미설정"
                          value={dayRule.half_leave_threshold === null || dayRule.half_leave_threshold === undefined ? '' : dayRule.half_leave_threshold}
                          disabled={!dayRule.is_workday}
                          onChange={(e) => onPatchRule(row._rowKey, dayKey, 'half_leave_threshold', e.target.value)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 주간 소정근로시간 + 연차 발생 여부 미리보기 */}
              {(() => {
                const preview = templateWeeklyPreview.find((p) => p.rowKey === row._rowKey);
                if (!preview) return null;
                const h = Math.floor(preview.weeklyMinutes / 60);
                const m = preview.weeklyMinutes % 60;
                return (
                  <div style={annualLeavePreviewStyle(preview.hasAnnualLeave)}>
                    <span style={{ fontWeight: 700 }}>
                      주간 소정근로: {h}시간 {String(m).padStart(2, '0')}분
                    </span>
                    <span style={{ marginLeft: 10, fontWeight: 700, color: preview.hasAnnualLeave ? '#16a34a' : '#dc2626' }}>
                      {preview.hasAnnualLeave ? '연차 발생 ✓' : '연차 미발생 ✗ (주 15시간 미만)'}
                    </span>
                  </div>
                );
              })()}
            </div>
          ))}

          <button style={btn} onClick={onAddRow}>+추가</button>
        </div>
      )}
    </Modal>
  );
}
